import React, { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import "./ConsultationPage.css";
import WCLogo1 from "../../assets/wealth_compounders_logo.png";
import {
  ToastContainer,
  SuccessModal,
  ErrorModal,
  useNotification,
} from "../NotificationSystem/NotificationSystem";
import { submitWebLead } from "../../api/webLeads";
import { resolveLeadModule } from "../../constants/webLeadModules";
import { formatConsultationInternalComments } from "../../utils/formatLeadComments";

const ConsultationPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const leadModule = resolveLeadModule(searchParams.get("module"));

  /* ── Notification state ── */
  const { toasts, removeToast, showWarning, showInfo } = useNotification();
  const [successModal, setSuccessModal] = useState({ open: false, data: null });
  const [errorModal, setErrorModal] = useState({
    open: false,
    title: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  /* ── Form state ── */
  const [step, setStep] = useState(1);
  const [selectedDate, setSelectedDate] = useState(null);
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    investmentGoal: "",
    portfolioSize: "",
    time: "",
    message: "",
  });

  /* ── Calendar helpers ── */
  const isWeekend = (date) => {
    const d = date.getDay();
    return d === 0 || d === 6;
  };
  const isBeforeToday = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const buildCalendarGrid = () => {
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const startOffset = firstDay === 0 ? 6 : firstDay - 1;
    const cells = [];
    for (let i = 0; i < startOffset; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
    return cells;
  };

  const prevMonth = () =>
    setCalendarMonth((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1));
  const nextMonth = () =>
    setCalendarMonth((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1));
  const canGoPrev = () => {
    const today = new Date();
    return (
      calendarMonth.getFullYear() > today.getFullYear() ||
      calendarMonth.getMonth() > today.getMonth()
    );
  };
  const isSameDay = (a, b) =>
    a &&
    b &&
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  const calendarCells = buildCalendarGrid();
  const MONTH_NAMES = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const DAY_HEADERS = ["Mon", "Tue", "Wed", "Thu", "Fri"];
  const timeSlots = [
    "09:00 AM",
    "10:00 AM",
    "11:00 AM",
    "01:00 PM",
    "02:00 PM",
    "03:00 PM",
  ];

  /* ── Handlers ── */
  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });
  const handleDateSelect = (date) => {
    if (!date || isWeekend(date) || isBeforeToday(date)) return;
    setSelectedDate(date);
  };

  /* ── Submission ── */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedDate) {
      setErrorModal({
        open: true,
        title: "Date Required",
        message:
          "Please select a consultation date from the calendar before confirming your booking.",
      });
      return;
    }
    if (!formData.time) {
      setErrorModal({
        open: true,
        title: "Time Slot Required",
        message:
          "Please choose a preferred time slot to complete your booking.",
      });
      return;
    }

    setSubmitting(true);
    try {
      await submitWebLead({
        fullName: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.replace(/\s/g, ""),
        module: leadModule,
        internalComments: formatConsultationInternalComments({
          leadModule,
          formData,
          selectedDate,
        }),
      });

      setSuccessModal({
        open: true,
        data: {
          name: formData.name,
          email: formData.email,
          date: selectedDate.toLocaleDateString("en-IN", {
            weekday: "long",
            day: "numeric",
            month: "long",
            year: "numeric",
          }),
          time: formData.time,
        },
      });
    } catch (err) {
      setErrorModal({
        open: true,
        title: "Submission Failed",
        message:
          err?.message ||
          "We could not save your booking. Please try again or contact support.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  /* ── Step navigation with error modals instead of alerts ── */
  const nextStep = () => {
    if (step === 1) {
      if (!formData.name) {
        setErrorModal({
          open: true,
          title: "Name Required",
          message: "Please enter your full name to continue.",
        });
        return;
      }
      if (!formData.email) {
        setErrorModal({
          open: true,
          title: "Email Required",
          message: "Please enter a valid email address to continue.",
        });
        return;
      }
      if (!formData.phone) {
        setErrorModal({
          open: true,
          title: "Phone Required",
          message:
            "Please enter your phone number so our advisor can reach you.",
        });
        return;
      }
      /* Show a friendly info toast as user moves forward */
      showInfo(
        "Looking good!",
        "Your details are saved. Let's set your investment goals.",
        3000,
      );
    }

    if (step === 2) {
      if (!formData.investmentGoal) {
        setErrorModal({
          open: true,
          title: "Goal Required",
          message:
            "Please select your primary investment goal so we can personalise your consultation.",
        });
        return;
      }
      showInfo(
        "Almost there!",
        "Now pick a date and time that works for you.",
        3000,
      );
    }

    setStep(step + 1);
  };

  const prevStep = () => {
    setStep(step - 1);
    showWarning("Going back", "Your progress is saved — don't worry!", 2500);
  };

  /* ─────────────────────────────────────────────────
     RENDER
  ───────────────────────────────────────────────── */
  return (
    <div className="wc-consultation-wrapper">
      {/* ── Global Toasts ── */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />

      {/* ── Success Modal ── */}
      <SuccessModal
        isOpen={successModal.open}
        data={successModal.data}
        onClose={() => setSuccessModal({ open: false, data: null })}
        onGoHome={() => {
          setSuccessModal({ open: false, data: null });
          navigate("/");
        }}
      />

      {/* ── Error / Validation Modal ── */}
      <ErrorModal
        isOpen={errorModal.open}
        title={errorModal.title}
        message={errorModal.message}
        onClose={() => setErrorModal({ open: false, title: "", message: "" })}
      />

      {/* ── Nav ── */}
      <header className="wc-consultation-nav">
        <div className="wc-consultation-nav-container">
          <Link to="/" className="wc-consultation-logo">
            <img
              src={WCLogo1}
              alt="Wealth Compounders"
              className="wc-consultation-logo-img"
            />
          </Link>

          <button
            onClick={() => navigate("/")}
            className="wc-consultation-back-btn"
          >
            <i className="fas fa-arrow-left"></i> Back to Home
          </button>
        </div>
      </header>

      <div className="wc-consultation-page-content">
        <div className="wc-consultation-container-full">
          {/* ── Left Sidebar ── */}
          <aside className="wc-consultation-sidebar">
            <div className="wc-consultation-sidebar-content">
              <span className="wc-consultation-badge">
                <i className="fas fa-calendar-check"></i> Book Your Session
              </span>
              <h1 className="wc-consultation-heading">
                Schedule Your
                <br />
                <span className="wc-gradient-text">Free Consultation</span>
              </h1>
              <p className="wc-consultation-description">
                Connect with our expert financial advisors and discover
                personalized strategies to grow your wealth.
              </p>
              <div className="wc-consultation-benefits-list">
                <h3>What You'll Get:</h3>
                {[
                  {
                    icon: "fa-chart-line",
                    title: "Portfolio Analysis",
                    desc: "Comprehensive review of your current investments",
                  },
                  {
                    icon: "fa-lightbulb",
                    title: "Custom Strategy",
                    desc: "Tailored wealth-building plan for your goals",
                  },
                  {
                    icon: "fa-handshake",
                    title: "Expert Guidance",
                    desc: "Direct access to our top advisors",
                  },
                ].map((b) => (
                  <div className="wc-consultation-benefit-item" key={b.title}>
                    <div className="wc-consultation-benefit-icon">
                      <i className={`fas ${b.icon}`}></i>
                    </div>
                    <div>
                      <h4>{b.title}</h4>
                      <p>{b.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </aside>

          {/* ── Right Form Panel ── */}
          <main className="wc-consultation-main">
            <div className="wc-consultation-form-container">
              {/* Progress Steps */}
              <div className="wc-consultation-steps">
                {[
                  { n: 1, label: "Details" },
                  { n: 2, label: "Goals" },
                  { n: 3, label: "Schedule" },
                ].map(({ n, label }, i) => (
                  <React.Fragment key={n}>
                    {i > 0 && <div className="wc-consultation-step-line"></div>}
                    <div
                      className={`wc-consultation-step-item ${step >= n ? "active" : ""} ${step > n ? "completed" : ""}`}
                    >
                      <div className="wc-consultation-step-circle">
                        {step > n ? <i className="fas fa-check"></i> : n}
                      </div>
                      <span>{label}</span>
                    </div>
                  </React.Fragment>
                ))}
              </div>

              <form
                onSubmit={handleSubmit}
                className="wc-consultation-form-content"
              >
                {/* ── Step 1 ── */}
                {step === 1 && (
                  <div className="wc-consultation-form-step">
                    <h2 className="wc-consultation-step-title">
                      Your Information
                    </h2>
                    {[
                      {
                        label: "Full Name *",
                        name: "name",
                        type: "text",
                        ph: "Your full name",
                      },
                      {
                        label: "Email Address *",
                        name: "email",
                        type: "email",
                        ph: "your@email.com",
                      },
                      {
                        label: "Phone Number *",
                        name: "phone",
                        type: "tel",
                        ph: "+91 98765 43210",
                      },
                    ].map((f) => (
                      <div className="wc-form-field" key={f.name}>
                        <label>{f.label}</label>
                        <input
                          type={f.type}
                          name={f.name}
                          value={formData[f.name]}
                          onChange={handleChange}
                          placeholder={f.ph}
                        />
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={nextStep}
                      className="wc-consultation-btn wc-btn-primary"
                    >
                      Continue <i className="fas fa-arrow-right"></i>
                    </button>
                  </div>
                )}

                {/* ── Step 2 ── */}
                {step === 2 && (
                  <div className="wc-consultation-form-step">
                    <h2 className="wc-consultation-step-title">
                      Investment Profile
                    </h2>

                    <div className="wc-form-field">
                      <label>Primary Investment Goal *</label>
                      <select
                        name="investmentGoal"
                        value={formData.investmentGoal}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select a goal...</option>
                        <option value="wealth-growth">
                          Long-term Wealth Growth
                        </option>
                        <option value="retirement">Retirement Planning</option>
                        <option value="income">Generate Passive Income</option>
                        <option value="preservation">
                          Wealth Preservation
                        </option>
                        <option value="education">Education Planning</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    <div className="wc-form-field">
                      <label>Current Portfolio Size</label>
                      <select
                        name="portfolioSize"
                        value={formData.portfolioSize}
                        onChange={handleChange}
                      >
                        <option value="">Select range...</option>
                        <option value="0-10L">₹0 – ₹10 Lakhs</option>
                        <option value="10L-50L">₹10 – ₹50 Lakhs</option>
                        <option value="50L-1Cr">₹50 Lakhs – ₹1 Crore</option>
                        <option value="1Cr-5Cr">₹1 – ₹5 Crores</option>
                        <option value="5Cr+">₹5 Crores+</option>
                      </select>
                    </div>

                    <div className="wc-form-field">
                      <label>Additional Information (Optional)</label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Tell us more about your financial goals..."
                        rows="4"
                      ></textarea>
                    </div>

                    <div className="wc-consultation-btn-group">
                      <button
                        type="button"
                        onClick={prevStep}
                        className="wc-consultation-btn wc-btn-secondary"
                      >
                        <i className="fas fa-arrow-left"></i> Back
                      </button>
                      <button
                        type="button"
                        onClick={nextStep}
                        className="wc-consultation-btn wc-btn-primary"
                      >
                        Continue <i className="fas fa-arrow-right"></i>
                      </button>
                    </div>
                  </div>
                )}

                {/* ── Step 3 ── */}
                {step === 3 && (
                  <div className="wc-consultation-form-step">
                    <h2 className="wc-consultation-step-title">
                      Pick Your Time
                    </h2>

                    {/* Calendar */}
                    <div className="wc-form-field">
                      <label>
                        Select Date{" "}
                        <span className="wc-label-note">
                          (Weekends unavailable)
                        </span>
                      </label>
                      <div className="wc-custom-calendar">
                        <div className="wc-cal-header">
                          <button
                            type="button"
                            className="wc-cal-nav-btn"
                            onClick={prevMonth}
                            disabled={!canGoPrev()}
                          >
                            <i className="fas fa-chevron-left"></i>
                          </button>
                          <span className="wc-cal-month-label">
                            {MONTH_NAMES[calendarMonth.getMonth()]}{" "}
                            {calendarMonth.getFullYear()}
                          </span>
                          <button
                            type="button"
                            className="wc-cal-nav-btn"
                            onClick={nextMonth}
                          >
                            <i className="fas fa-chevron-right"></i>
                          </button>
                        </div>

                        <div className="wc-cal-dow-row">
                          {DAY_HEADERS.map((d) => (
                            <div key={d} className="wc-cal-dow">
                              {d}
                            </div>
                          ))}
                        </div>

                        <div className="wc-cal-grid">
                          {calendarCells
                            .filter((cell) => cell === null || !isWeekend(cell))
                            .map((cell, idx) => {
                              if (!cell)
                                return (
                                  <div
                                    key={`blank-${idx}`}
                                    className="wc-cal-cell wc-cal-blank"
                                  />
                                );
                              const past = isBeforeToday(cell);
                              const selected = isSameDay(cell, selectedDate);
                              return (
                                <button
                                  key={cell.toISOString()}
                                  type="button"
                                  disabled={past}
                                  onClick={() => handleDateSelect(cell)}
                                  className={`wc-cal-cell wc-cal-day ${past ? "wc-cal-past" : ""} ${selected ? "wc-cal-selected" : ""}`}
                                >
                                  <span className="wc-cal-num">
                                    {cell.getDate()}
                                  </span>
                                </button>
                              );
                            })}
                        </div>

                        {selectedDate && (
                          <div className="wc-cal-selected-label">
                            <i className="fas fa-calendar-check"></i>
                            {selectedDate.toLocaleDateString("en-IN", {
                              weekday: "long",
                              day: "numeric",
                              month: "long",
                              year: "numeric",
                            })}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Time slots */}
                    <div className="wc-form-field">
                      <label>Select Time Slot *</label>
                      <div className="wc-time-slots-grid">
                        {timeSlots.map((time) => (
                          <button
                            key={time}
                            type="button"
                            className={`wc-time-slot ${formData.time === time ? "selected" : ""}`}
                            onClick={() => setFormData({ ...formData, time })}
                          >
                            <i className="fas fa-clock"></i> {time}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Booking Summary */}
                    {selectedDate && formData.time && (
                      <div className="wc-booking-summary">
                        <h4>Booking Summary</h4>
                        {[
                          { icon: "fa-user", val: formData.name },
                          { icon: "fa-envelope", val: formData.email },
                          {
                            icon: "fa-calendar",
                            val: selectedDate.toLocaleDateString("en-IN", {
                              weekday: "short",
                              day: "numeric",
                              month: "short",
                            }),
                          },
                          { icon: "fa-clock", val: formData.time },
                        ].map((r) => (
                          <div className="wc-summary-row" key={r.icon}>
                            <i className={`fas ${r.icon}`}></i>
                            <span>{r.val}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="wc-consultation-btn-group">
                      <button
                        type="button"
                        onClick={prevStep}
                        className="wc-consultation-btn wc-btn-secondary"
                      >
                        <i className="fas fa-arrow-left"></i> Back
                      </button>
                      <button
                        type="submit"
                        className="wc-consultation-btn wc-btn-success"
                        disabled={!selectedDate || !formData.time || submitting}
                      >
                        <i className="fas fa-check-circle"></i>{" "}
                        {submitting ? "Submitting…" : "Confirm Booking"}
                      </button>
                    </div>
                  </div>
                )}
              </form>

              {/* Trust Badges */}
              <div className="wc-consultation-trust-badges">
                {[
                  { icon: "fa-shield-halved", label: "100% Secure" },
                  { icon: "fa-lock", label: "Data Protected" },
                  { icon: "fa-star", label: "4.9/5 Rating" },
                ].map((b) => (
                  <div className="wc-trust-badge" key={b.label}>
                    <i className={`fas ${b.icon}`}></i> <span>{b.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default ConsultationPage;
